/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { rem, useMantineTheme, Text, Group, Center } from '@mantine/core';
import { useEffect, type ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { isFinetuningDeployable } from '@fai/friendli-suite/features/products/dedicated-endpoints/finetuning/finetuning-status';
import { useFinetuningOutputModels } from '@fai/friendli-suite/features/products/dedicated-endpoints/finetuning/use-finetuning-output-models';
import { useFinetuningStatus } from '@fai/friendli-suite/features/products/dedicated-endpoints/finetuning/use-finetuning-status';
import Paths from '@fai/friendli-suite/features/routes/paths';
import DEProjectSubLayout from '@fai/friendli-suite/views/layout/product/dedicated-endpoints/projects/sub-layout';
import { NavigationTabs } from '@fai/ui/components/tabs/navigation-tabs';
import { TextVariants } from '@fai/ui/components/typography/variants';

import FinetuningDetailHeader from './finetuning-detail-header';

export default function ProjectFinetuningDetailLayout() {
  const { t } = useTranslation();
  const { teamId = '', projectId = '', finetuningId = '' } = useParams();
  const theme = useMantineTheme();

  const { status } = useFinetuningStatus();
  const {
    totalCount: outputModelCount,
    startPolling: startOutputModelsPolling,
    stopPolling: stopOutputModelsPolling,
  } = useFinetuningOutputModels();

  useEffect(() => {
    if (isFinetuningDeployable(status)) {
      startOutputModelsPolling(1000);
    } else {
      stopOutputModelsPolling();
    }

    return stopOutputModelsPolling;
  }, [status, startOutputModelsPolling, stopOutputModelsPolling]);

  const basePath = Paths.team(teamId)
    .dedicatedEndpoints.project.detail(projectId)
    .finetuning.detail(finetuningId);

  const tabValues: ComponentProps<typeof NavigationTabs>['values'] = [
    {
      label: t('dedicated_endpoints.project.overview'),
      path: basePath.overview,
    },
    {
      label: t('dedicated_endpoints.project.output_models'),
      disabled: !isFinetuningDeployable(status) || outputModelCount <= 0,
      path: basePath['output-models'],
    },
  ];

  return (
    <DEProjectSubLayout
      headerSlot={<FinetuningDetailHeader outputModelCount={outputModelCount} />}
      tabSlot={
        <NavigationTabs
          values={tabValues}
          renderTab={({ label, path }) => (
            <Group gap={theme.pf.layout['4xs']}>
              <Text variant={TextVariants.TitleSm}>{label}</Text>

              {path === basePath['output-models'] && outputModelCount > 0 && (
                <Center
                  bd={`1px solid ${theme.pf.color.border.neutral.default}`}
                  mih={rem(20)}
                  miw={rem(20)}
                  px={theme.pf.layout['4xs']}
                  style={{ borderRadius: theme.pf.radius.sm }}
                >
                  <Text variant={TextVariants.Title2Xs}>{outputModelCount}</Text>
                </Center>
              )}
            </Group>
          )}
          preventScrollReset={true}
        />
      }
    />
  );
}

ProjectFinetuningDetailLayout.displayName = 'ProjectFinetuningDetailLayout';
